import { useMemo } from 'react';
import { Catalog } from '../../../apis/search-catalog';
import {
  SequentialSource,
  useSequentialSources,
} from '../../sequential-sources';
import {
  CatalogIdentifierStrategy,
  getCatalogIdentifier,
} from './strategies/identifier-strategies';
import {
  useMostAccessedCatalogsByCategorySource,
  useMostAccessedCatalogsByDepartmentSource,
  useMostAccessedCatalogsByDescriptionSource,
  useMostAccessedCatalogsByTagsSource,
  useMostAccessedCatalogsByTitleSource,
  UseMostAccessedCatalogsSourceParams,
} from './sources';

interface UseMostAccessedCatalogsByFieldsOptions {
  identifierStrategy?: CatalogIdentifierStrategy;
}

export type UseMostAccessedCatalogsByFieldsParams =
  UseMostAccessedCatalogsSourceParams;

export const useMostAccessedCatalogsByFields = (
  params: UseMostAccessedCatalogsByFieldsParams,
  {
    identifierStrategy = getCatalogIdentifier,
  }: UseMostAccessedCatalogsByFieldsOptions = {}
) => {
  // The order of the sources is important
  const titleSource = useMostAccessedCatalogsByTitleSource(params);

  const tagsSource = useMostAccessedCatalogsByTagsSource(params, {
    enabled: areAllSourcesExhausted(titleSource),
  });

  const descriptionSource = useMostAccessedCatalogsByDescriptionSource(params, {
    enabled: areAllSourcesExhausted(titleSource, tagsSource),
  });

  const categorySource = useMostAccessedCatalogsByCategorySource(params, {
    enabled: areAllSourcesExhausted(titleSource, tagsSource, descriptionSource),
  });

  const departmentSource = useMostAccessedCatalogsByDepartmentSource(params, {
    enabled: areAllSourcesExhausted(
      titleSource,
      tagsSource,
      descriptionSource,
      categorySource
    ),
  });

  const sources = useMemo(
    () => [
      titleSource,
      tagsSource,
      descriptionSource,
      categorySource,
      departmentSource,
    ],
    [
      titleSource,
      tagsSource,
      descriptionSource,
      categorySource,
      departmentSource,
    ]
  );

  return useSequentialSources<Catalog>({
    sources,
    getIdentifier: identifierStrategy,
  });
};

const areAllSourcesExhausted = (...sources: SequentialSource<Catalog>[]) => {
  return sources.every((source) => isSourceExhausted(source));
};

const isSourceExhausted = (source: {
  hasMore: boolean;
  isLoading: boolean;
  isFetchingMore: boolean;
}) => !source.hasMore && !source.isLoading && !source.isFetchingMore;
